import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import { useAuth } from "@/lib/auth";
import { Database, FileText, FileJson, ShieldCheck, ChevronDown, ChevronUp, Filter } from "lucide-react";

const LABELS = {
  sites: "Sites",
  trucks: "Trucks",
  drivers: "Drivers",
  projects: "Projects",
  haulers: "Haulers",
  loads: "Loads (with custody events)",
  invoices: "Invoices (with line items)",
  audit_log: "Audit Log",
  users: "Users (password hashes excluded)",
};

const DESCRIPTIONS = {
  sites: "Construction, dump-yard and fill-site directory",
  trucks: "Fleet with plate, capacity, DOT, tare weight, hauler linkage",
  drivers: "CDL roster with expiries and hauler linkage",
  projects: "Capital projects with budgets and approved-drop whitelist",
  haulers: "Registered carriers with insurance and approval state",
  loads: "All haul jobs with full chain-of-custody trail",
  invoices: "Billing records with line items and totals",
  audit_log: "Immutable trail — every create/update/delete/approve/export",
  users: "User accounts and roles (safe — no password hashes)",
};

// Status vocabularies per collection — feeds the status dropdown when the
// collection supports status filtering. Keep in sync with backend schemas.
const STATUS_OPTIONS = {
  loads: ["pending", "loading", "in_transit", "arriving", "delivered", "cancelled"],
  invoices: ["draft", "pending_review", "approved", "paid", "disputed", "matched"],
};

function buildQuery(filters) {
  const p = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v && String(v).trim()) p.set(k, v);
  });
  return p.toString();
}

/** Per-row expandable filter panel + download buttons. */
function ExportRow({ row, backend }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", status: "",
    project_id: "", hauler_id: "",
  });
  const [refs, setRefs] = useState({ projects: [], haulers: [] });

  const supports = new Set(row.filters || []);
  const anyFilterApplied = Object.values(filters).some((v) => v && v.trim());

  // Lazy-load project/hauler options only when the row is first opened AND
  // this collection supports one of those filters.
  useEffect(() => {
    if (!open) return;
    const needProjects = supports.has("project_id") && refs.projects.length === 0;
    const needHaulers = supports.has("hauler_id") && refs.haulers.length === 0;
    if (!needProjects && !needHaulers) return;
    (async () => {
      const next = { ...refs };
      try {
        if (needProjects) next.projects = (await api.get("/projects")).data || [];
        if (needHaulers) next.haulers = (await api.get("/haulers")).data || [];
        setRefs(next);
      } catch {
        // silent — dropdowns will just remain empty
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const qs = buildQuery(filters);
  const csvUrl = `${backend}/api/export/${row.collection}?format=csv${qs ? `&${qs}` : ""}`;
  const jsonUrl = `${backend}/api/export/${row.collection}?format=json${qs ? `&${qs}` : ""}`;
  const disabled = row.count === 0;

  return (
    <div className="bg-white border border-gray-200" data-testid={`export-row-${row.collection}`}>
      <div className="p-5 flex items-center justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-3">
            <div className="font-display font-bold text-gray-900 text-base">
              {LABELS[row.collection] || row.collection}
            </div>
            <span className="font-mono text-xs px-2 py-0.5 bg-gray-100 text-gray-700">
              {row.count.toLocaleString()} rows
            </span>
            {anyFilterApplied && (
              <span className="font-mono text-[10px] px-2 py-0.5 bg-orange-100 text-orange-800 uppercase tracking-widest">
                filtered
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-1">{DESCRIPTIONS[row.collection]}</div>
        </div>
        <div className="flex items-center gap-2">
          {row.filters?.length > 0 && (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              data-testid={`export-filters-toggle-${row.collection}`}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 text-[11px] uppercase tracking-widest font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-3.5 h-3.5" />
              Filters
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
          <a
            href={csvUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`export-csv-${row.collection}`}
            className={`flex items-center gap-1.5 px-3 py-2 border text-[11px] uppercase tracking-widest font-semibold transition-colors ${
              disabled
                ? "border-gray-200 text-gray-400 pointer-events-none"
                : "border-gray-300 text-gray-800 hover:bg-gray-900 hover:text-white hover:border-gray-900"
            }`}
          >
            <FileText className="w-3.5 h-3.5" /> CSV
          </a>
          <a
            href={jsonUrl}
            target="_blank"
            rel="noreferrer"
            data-testid={`export-json-${row.collection}`}
            className={`flex items-center gap-1.5 px-3 py-2 border text-[11px] uppercase tracking-widest font-semibold transition-colors ${
              disabled
                ? "border-gray-200 text-gray-400 pointer-events-none"
                : "border-orange-500 text-orange-700 bg-orange-50 hover:bg-orange-500 hover:text-white"
            }`}
          >
            <FileJson className="w-3.5 h-3.5" /> JSON
          </a>
        </div>
      </div>
      {open && (
        <div className="border-t border-gray-200 p-5 bg-gray-50" data-testid={`export-filters-${row.collection}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {supports.has("date") && (
              <>
                <div>
                  <label className="label-caps block mb-1">Created from</label>
                  <input
                    type="date"
                    value={filters.date_from}
                    onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    data-testid={`filter-date-from-${row.collection}`}
                    className="input-sharp w-full"
                  />
                </div>
                <div>
                  <label className="label-caps block mb-1">Created to</label>
                  <input
                    type="date"
                    value={filters.date_to}
                    onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    data-testid={`filter-date-to-${row.collection}`}
                    className="input-sharp w-full"
                  />
                </div>
              </>
            )}
            {supports.has("status") && (
              <div>
                <label className="label-caps block mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  data-testid={`filter-status-${row.collection}`}
                  className="input-sharp w-full"
                >
                  <option value="">Any</option>
                  {(STATUS_OPTIONS[row.collection] || []).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            )}
            {supports.has("project_id") && (
              <div>
                <label className="label-caps block mb-1">Project</label>
                <select
                  value={filters.project_id}
                  onChange={(e) => setFilters({ ...filters, project_id: e.target.value })}
                  data-testid={`filter-project-${row.collection}`}
                  className="input-sharp w-full"
                >
                  <option value="">Any</option>
                  {refs.projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.name || p.id}</option>
                  ))}
                </select>
              </div>
            )}
            {supports.has("hauler_id") && (
              <div>
                <label className="label-caps block mb-1">Hauler</label>
                <select
                  value={filters.hauler_id}
                  onChange={(e) => setFilters({ ...filters, hauler_id: e.target.value })}
                  data-testid={`filter-hauler-${row.collection}`}
                  className="input-sharp w-full"
                >
                  <option value="">Any</option>
                  {refs.haulers.map((h) => (
                    <option key={h.id} value={h.id}>{h.name || h.id}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {anyFilterApplied && (
            <button
              type="button"
              onClick={() => setFilters({ date_from: "", date_to: "", status: "", project_id: "", hauler_id: "" })}
              data-testid={`filter-clear-${row.collection}`}
              className="mt-3 text-xs uppercase tracking-widest font-semibold text-orange-700 hover:text-orange-900"
            >
              Clear filters
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Export() {
  const { user } = useAuth();
  const [summary, setSummary] = useState([]);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/export/summary");
      setSummary(data);
      setError("");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const isAdmin = user?.role === "admin";

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="Data Export" subtitle="Backup & audit" />
        <div className="p-8">
          <div className="bg-white border border-gray-200 p-8 text-center">
            <ShieldCheck className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <div className="text-sm text-gray-600">Admin access required.</div>
          </div>
        </div>
      </div>
    );
  }

  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <div>
      <PageHeader
        title="Data Export"
        subtitle="Backup & audit"
        actions={
          <div className="flex items-center gap-2 text-xs font-mono text-gray-600">
            <Database className="w-4 h-4 text-emerald-600" />
            <span>{summary.reduce((s, r) => s + r.count, 0).toLocaleString()} total rows</span>
          </div>
        }
      />
      <div className="p-8">
        {error && <div className="mb-4 px-3 py-2 bg-red-50 border border-red-200 text-red-800 text-sm">{error}</div>}

        <div className="mb-6 p-4 bg-white border border-gray-200">
          <div className="text-sm text-gray-700 leading-relaxed">
            Download a full or filtered snapshot of any collection as <strong>CSV</strong> (opens in Excel/Sheets) or <strong>JSON</strong> (includes nested arrays like custody events and invoice line items). Click <strong>Filters</strong> on any row to narrow by date, status, project, or hauler. Every export writes a record to the Audit Log.
          </div>
        </div>

        <div className="space-y-3" data-testid="export-list">
          {summary.map((row) => (
            <ExportRow key={row.collection} row={row} backend={backend} />
          ))}
        </div>
      </div>
    </div>
  );
}
